import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Instagram, Youtube, Twitch, Music2, Play, BadgeCheck, ExternalLink, ScrollText, Mail, Radio, Twitter } from "lucide-react";
import { useContent } from "../context/ContentContext";
import Events from "../components/Events";

const slugify = (s = "") =>
  s.toString().toLowerCase().trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const CHANNEL_META = {
  instagram: { Icon: Instagram, label: "INSTAGRAM", unit: "FOLLOWER", bg: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]", ico: "text-white" },
  tiktok:    { Icon: Music2,    label: "TIKTOK",    unit: "FOLLOWER", bg: "bg-black border-white/20", ico: "text-white" },
  youtube:   { Icon: Youtube,   label: "YOUTUBE",   unit: "ABONNENTEN", bg: "bg-[#FF0000]", ico: "text-white" },
  twitch:    { Icon: Twitch,    label: "TWITCH",    unit: "FOLLOWER", bg: "bg-[#9146FF]", ico: "text-white" },
  spotify:   { Icon: Music2,    label: "SPOTIFY",   unit: "HÖRER", bg: "bg-[#1DB954]", ico: "text-black" },
  apple:     { Icon: Music2,    label: "APPLE MUSIC", unit: "HÖRER", bg: "bg-gradient-to-br from-[#FA233B] to-[#FB5C74]", ico: "text-white" },
  soundcloud:{ Icon: Radio,     label: "SOUNDCLOUD", unit: "FOLLOWER", bg: "bg-[#FF5500]", ico: "text-white" },
  x:         { Icon: Twitter,   label: "X (TWITTER)", unit: "FOLLOWER", bg: "bg-black border-white/20", ico: "text-white" },
  twitter:   { Icon: Twitter,   label: "X (TWITTER)", unit: "FOLLOWER", bg: "bg-black border-white/20", ico: "text-white" },
  facebook:  { Icon: ExternalLink, label: "FACEBOOK", unit: "FOLLOWER", bg: "bg-[#1877F2]", ico: "text-white" },
  other:     { Icon: ExternalLink, label: "SONSTIGES", unit: "FOLLOWER", bg: "bg-[#ff0033]/15 border-[#ff0033]/40", ico: "text-[#ff0033]" },
};

const TYPE_LABEL = { artist: "KÜNSTLER", creator: "CREATOR", producer: "PRODUZENT" };
const TYPE_KEY   = { artist: "artists",  creator: "creators", producer: "producers" };
const TYPE_BASE  = { artist: "/#kuenstler", creator: "/#creator", producer: "/#produzenten" };

export default function PersonPage({ type }) {
  const { slug } = useParams();
  const { content } = useContent();
  const list = content?.[TYPE_KEY[type]] || [];
  const person = list.find((p) => (p.slug || slugify(p.name)) === slug);

  if (!person) {
    return (
      <section className="min-h-screen bg-[#050505] py-32 px-6">
        <div className="max-w-4xl mx-auto text-white">„{slug}" nicht gefunden. <Link to="/" className="text-[#ff0033]">Home</Link></div>
      </section>
    );
  }

  const channels = person.channels || [];
  const partners = person.partners || [];
  const songs = person.songs || [];
  const events = person.events || [];
  const email = person.impressum_email || person.email || "";
  const cta = person.music_url || person.instagram_url || "";
  const ctaLabel = person.music_label || (person.instagram_url ? "ZUM INSTAGRAM" : "");
  const hasImpressum = type === "creator" || person.impressum;

  return (
    <section className="relative min-h-screen bg-[#050505] py-16 md:py-24 overflow-hidden grain">
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">

        {/* Breadcrumb */}
        <nav data-testid="breadcrumb" className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-600 mb-10">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={11} />
          <Link to={TYPE_BASE[type]} className="hover:text-white transition-colors">{TYPE_LABEL[type]}</Link>
          <ChevronRight size={11} />
          <span className="text-[#ff0033]">{person.name}</span>
        </nav>

        {/* Hero-Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 bg-[#0A0A0A] border border-white/[0.06] overflow-hidden"
        >
          <div className="relative aspect-square md:aspect-auto md:min-h-[520px] bg-gradient-to-br from-[#1a0505] via-[#0A0A0A] to-[#050505] overflow-hidden">
            {person.image ? (
              <img src={person.image} alt={person.name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[10px] tracking-[0.3em] uppercase text-zinc-700 font-bold">Kein Foto</div>
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/40" />
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-4">
              {TYPE_LABEL[type]}
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl text-white flex items-center gap-3 flex-wrap">
              <span>{person.name}</span>
              {person.verified && <BadgeCheck size={32} className="text-[#ff0033] fill-[#ff0033]/20" data-testid="verified-badge" />}
            </h1>
            <div className="mt-4 w-16 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
            {person.bio && (
              <p className="mt-8 text-zinc-300 text-base leading-relaxed whitespace-pre-line">
                {person.bio}
              </p>
            )}
            {cta && (
              <a href={cta} target="_blank" rel="noopener noreferrer" data-testid="hero-cta"
                 className="mt-8 inline-flex items-center gap-3 w-fit border border-[#ff0033]/50 hover:bg-[#ff0033] hover:text-white text-white px-6 py-3.5 text-[11px] tracking-[0.3em] uppercase font-bold transition-all hover:shadow-[0_0_24px_rgba(255,0,51,0.4)]">
                {ctaLabel || "MEHR ERFAHREN"}
                <ExternalLink size={12} />
              </a>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              {hasImpressum && (
                <Link to={`/creator/${person.slug || slugify(person.name)}/impressum`}
                      className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/60 px-4 py-2 text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-400 hover:text-white transition-colors">
                  <ScrollText size={12} /> Impressum
                </Link>
              )}
              {email && (
                <a href={`mailto:${email}`}
                   className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/60 px-4 py-2 text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-400 hover:text-white transition-colors">
                  <Mail size={12} /> Kontakt
                </a>
              )}
            </div>
          </div>
        </motion.div>

        {/* Community */}
        {channels.length > 0 && (
          <div className="mt-6 bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-8">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-6">Community</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {channels.map((ch, i) => {
                const meta = CHANNEL_META[ch.platform] || CHANNEL_META.other;
                const Icon = meta.Icon;
                const Card = ch.url ? "a" : "div";
                const props = ch.url ? { href: ch.url, target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                    <Card {...props} data-testid={`channel-${ch.platform}`}
                          className="block bg-[#050505] border border-white/10 hover:border-[#ff0033]/50 p-5 transition-colors group">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 flex items-center justify-center shrink-0 border ${meta.bg}`}>
                          <Icon size={18} className={meta.ico} />
                        </div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 font-bold truncate">
                          {ch.label || meta.label}
                        </div>
                      </div>
                      <div className="font-display font-black text-3xl md:text-4xl text-white tracking-tight">
                        {ch.followers || "—"}
                      </div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mt-1">
                        {ch.unit || meta.unit}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Songs */}
        {songs.length > 0 && (
          <div className="mt-6 bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-8">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-6 flex items-center gap-2">
              <Play size={13} /> Songs · Streams
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {songs.map((s, i) => {
                const Card = s.url ? "a" : "div";
                const props = s.url ? { href: s.url, target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                  <Card key={i} {...props} className="flex items-center gap-4 bg-[#050505] border border-white/10 hover:border-[#ff0033]/50 p-4 transition-colors group">
                    {s.cover ? (
                      <img src={s.cover} alt={s.title} className="w-14 h-14 object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 bg-[#0A0A0A] border border-white/5 flex items-center justify-center shrink-0">
                        <Music2 size={18} className="text-zinc-700" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-medium truncate">{s.title}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mt-1">
                        {s.streams ? `${s.streams} Streams` : ""}{s.streams && s.released ? " · " : ""}{s.released || ""}
                      </div>
                    </div>
                    {s.url && <ExternalLink size={12} className="text-zinc-600 group-hover:text-[#ff0033] transition-colors shrink-0" />}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Partnerschaften */}
        {partners.length > 0 && (
          <div className="mt-6 bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-8">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-6">Partnerschaften</div>
            <div className="space-y-3">
              {partners.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                            data-testid={`partner-${i}`}
                            className="grid grid-cols-1 md:grid-cols-[220px_1fr_160px] gap-4 md:gap-6 items-center bg-[#050505] border border-white/10 hover:border-[#ff0033]/40 p-5 transition-colors">
                  <div className="flex items-center justify-start md:justify-center min-h-[48px]">
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="max-h-10 max-w-[180px] object-contain grayscale hover:grayscale-0 transition-all" />
                    ) : (
                      <div className="font-display font-black uppercase tracking-tight text-xl text-white">{p.name}</div>
                    )}
                  </div>
                  <div className="text-sm text-zinc-300 leading-relaxed">
                    {p.description || p.name}
                    {p.since && <span className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mt-1">seit {p.since}</span>}
                  </div>
                  <div className="flex justify-start md:justify-end">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-2 border border-[#ff0033]/50 hover:bg-[#ff0033] hover:text-white text-white px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase font-bold transition-all">
                        {p.cta_label || "Zum Shop"} <ExternalLink size={11} />
                      </a>
                    ) : (
                      <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 font-bold">{p.type || "Kooperation"}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Events (per Person) */}
        <Events events={events} />

      </div>
    </section>
  );
}
